import { useEffect, useState } from "react";
import axios from "axios";
import { Activity, LogOut, ShieldCheck } from "lucide-react";
import { Input } from "@/components/ui/input";
import { API } from "@/lib/api";
import { useAuth, formatErr } from "@/lib/auth";
import CopyAddress from "@/components/whale/CopyAddress";

export default function PayPage() {
  const { user, logout, reload } = useAuth();
  const [info, setInfo] = useState(null);
  const [sig, setSig] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { axios.get(`${API}/payments/quote`).then((r) => setInfo(r.data)); }, []);

  const verify = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      await axios.post(`${API}/payments/verify`, { signature: sig });
      await reload();
    } catch (e2) { setErr(formatErr(e2)); }
    setBusy(false);
  };

  return (
    <div className="terminal-bg flex items-center justify-center px-4 py-10">
      <div className="panel rise w-full max-w-lg space-y-6 p-7" data-testid="pay-page">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Activity className="h-5 w-5 text-[#00E5FF]" />
            <span className="font-display font-bold">WHALE<span className="text-[#00E5FF]">/</span>FLOW</span>
          </div>
          <button onClick={logout} data-testid="pay-logout-button" className="flex items-center gap-1 text-xs text-slate-400 hover:text-white">
            <LogOut className="h-3.5 w-3.5" /> {user?.email}
          </button>
        </div>
        <div>
          <p className="overline">unlock lifetime access</p>
          <h1 className="mt-2 font-display text-2xl font-bold">
            Send exactly <span className="font-mono text-[#00E5FF]" data-testid="pay-amount">{info?.exact_amount ?? "…"} USDC</span> on Solana
          </h1>
          <p className="mt-2 text-sm text-slate-300">
            This amount is unique to your account, so your payment can only unlock your account. Enter all 4 decimals when you send.
          </p>
          {info && <CopyAddress address={info.exact_amount} testid="pay-exact-amount" />}
          <p className="mt-2 text-sm text-[#FFB300]">Only USDC (SPL token) on the Solana network. Other tokens or networks cannot be recovered.</p>
        </div>
        <div>
          <p className="overline">payment address</p>
          {info && <CopyAddress address={info.address} testid="pay-address" />}
          <p className="mt-2 font-mono text-[10px] text-slate-500">USDC mint: {info?.mint}</p>
        </div>
        <form onSubmit={verify} className="space-y-3 border-t border-[#1E2738] pt-5">
          <p className="text-sm text-slate-300">After sending, paste the transaction signature (from Phantom, Solflare or Solscan):</p>
          <Input data-testid="pay-signature-input" required value={sig} onChange={(e) => setSig(e.target.value)}
            placeholder="e.g. 5h3k…Qz9" className="border-[#2E3C54] bg-[#0F141C] font-mono text-xs" />
          {err && <p className="text-sm text-[#FF3D00]" data-testid="pay-error">{err}</p>}
          <button data-testid="pay-verify-button" disabled={busy} className="flex w-full items-center justify-center gap-2 rounded-md bg-[#00E5FF] py-2.5 font-semibold text-[#080B10] transition-opacity hover:opacity-90 disabled:opacity-50">
            <ShieldCheck className="h-4 w-4" /> {busy ? "Verifying on-chain…" : "Verify payment"}
          </button>
        </form>
      </div>
    </div>
  );
}
